import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useContext } from 'react';

import { useAppDispatch, useAppSelector } from '../app/hooks';
import { getOperationKey, stringsEqualIgnoreCase } from '../util';

import PrimaryButton from '../ui/PrimaryButton';
import SecondaryButton from '../ui/SecondaryButton';
import WhiteButton from '../ui/WhiteButton';
import LightBlueButton from '../ui/LightBlueButton';

import { ScreenStackItem } from '../features/screenStackSlice';
import { replaceJobMenuTimestamp } from '../features/jobMenuSlice';
import IfmsButton from '../ui/IfmsButton';
import useJobMenus from '../hooks/useJobMenus';
import ApplicationContext from '../ApplicationContext';
import AddIcon2 from '../img/Add2.svg';
import AddIcon2_D from '../img/Add_Dark2.svg';
import AddIcon3 from '../img/toolbar-new-light.svg';
import AddIcon3_D from '../img/toolbar-new-dark.svg';
import AddIcon4 from '../img/new_light.svg';
import AddIcon4_D from '../img/new_dark.svg';
import AddIcon5 from '../img/new-light.svg';
import AddIcon5_D from '../img/new-dark.svg';
import EditIcon2 from '../img/Edit2.svg';
import EditIcon2_D from '../img/Edit_Dark2.svg';
import EditIcon3 from '../img/toolbar-edit-light.svg';
import EditIcon3_D from '../img/toolbar-edit-dark.svg';
import EditIcon4 from '../img/edit_light.svg';
import EditIcon4_D from '../img/edit_dark_accent.svg';
import EditIcon4_DA from '../img/edit_dark_accent.svg';
import EditIcon5 from '../img/edit-light.svg';
import EditIcon5_D from '../img/edit-dark-accent.svg';
import EditIcon5_DA from '../img/edit-dark-accent.svg';
import ViewIcon2 from '../img/View3.svg';
import ViewIcon2_D from '../img/View3_Dark.svg';
import ViewIcon3 from '../img/toolbar-view-light.svg';
import ViewIcon3_D from '../img/toolbar-view-dark.svg';
import ViewIcon4 from '../img/view_light.svg';
import ViewIcon4_D from '../img/View_Dark.svg';
import ViewIcon4_DA from '../img/view_dark_accent.svg';
import ViewIcon5 from '../img/view-light.svg'
import ViewIcon5_D from '../img/view-dark.svg';
import ViewIcon5_DA from '../img/view-dark-accent.svg';
import CreditIcon2 from '../img/Credit2.svg';
import CreditIcon2_D from '../img/Credit_Dark2.svg';
import GCreditIcon2 from '../img/Give Credit2.svg';
import GCreditIcon2_D from '../img/Give Credit_Dark2.svg';

type UniqueLinkButtonProps = {
  link: string,
  viewLink?: string,
  operation?: string,
  label: React.ReactNode|string,
  beforeRedirect?: (() => Promise<boolean | string>)|undefined,
  uniqueLinkPrefixQuery?: ((x:ScreenStackItem) => boolean) | string | null,
  disabled?: boolean,
  display?: 'primary' | 'secondary' | 'white' | 'lightblue',
  baseClass?: string,
  useOnlyIcon? : boolean,
  iconMode?: boolean,
  isDetail?: boolean,
  [x:string]: any
  newStyle?: boolean,
};

function UniqueLinkButton({
  link,
  viewLink,
  operation,
  label,
  beforeRedirect = undefined,
  uniqueLinkPrefixQuery = undefined,
  disabled = false,
  display = 'primary',
  baseClass,
  goToAlreadyOpenPage = false,
  isMainOp = false,
  useOnlyIcon = false,
  iconMode = false,
  isDetail = false,
  style,
  newStyle = false,
  ...other }: UniqueLinkButtonProps) {
  const dispatch = useAppDispatch();

  const jobMenu = useAppSelector(state => state.jobMenu);
  const screenStack = useAppSelector(x => x.screenStack);
  const operationState = useAppSelector(x => x.operation);
  const activeOperation = operationState.operation;
  const appcontext = useContext(ApplicationContext);
  const { moveToSingleOpenedScreenInAnyOperationStartingWithUrl } = useJobMenus();

  let activeOperationKey = getOperationKey(activeOperation);
  if (!activeOperationKey) {
    if (activeOperation === 'Reference Files' || activeOperation === 'Settings' || activeOperation === 'Utilities') {
      activeOperationKey = getOperationKey('Standalone');
    }
  }

  const isDisabled = disabled || ( !isMainOp && (
    !!activeOperationKey &&
    (
      !!uniqueLinkPrefixQuery
        ? (
          _.isString(uniqueLinkPrefixQuery)
            ? screenStack[activeOperationKey].some(x => x.url && x.url.toLowerCase().startsWith(uniqueLinkPrefixQuery.toLowerCase()))
            : screenStack[activeOperationKey].some(x => uniqueLinkPrefixQuery(x))
        )
        : screenStack[activeOperationKey].some(x => stringsEqualIgnoreCase(x.url, link))
    )
  ));

  function OpenNewPageAsync(newLink: string = '') {
    const tempLink = _.isEmpty(newLink) ? link : newLink
    let urls = [tempLink];
    if (viewLink) urls.push(viewLink);
    if (goToAlreadyOpenPage) {
      if (tempLink.indexOf('/edit') > -1) urls.push(tempLink.replace('/edit', '/view'));
      if (tempLink.indexOf('/view') > -1) urls.push(tempLink.replace('/view', '/edit'));
    }

    moveToSingleOpenedScreenInAnyOperationStartingWithUrl(tempLink, urls)
    return Promise.resolve(true);
  }

  async function handleClick() {
    let continueRedirect: boolean | string = false;

    if (beforeRedirect) {
      continueRedirect = await beforeRedirect();
      if (!continueRedirect) {
        return;
      }
    }

    if (typeof continueRedirect === 'string')
      continueRedirect = await OpenNewPageAsync(continueRedirect);
    else
      continueRedirect = await OpenNewPageAsync();

    if (!continueRedirect) {
      return;
    }

    if (viewLink) {
      const viewModeTimestamp = jobMenu[String(operation)].find(y => (y.url === viewLink))?.timestamp
      if (viewModeTimestamp)
        dispatch(replaceJobMenuTimestamp({ url: link, timestamp: viewModeTimestamp }));
    }
  }

  if (useOnlyIcon) {
    const tooltip = other['data-tooltip'];
    if (tooltip === 'New' || tooltip === 'Amend' || tooltip === 'Edit' || tooltip === 'View') {
      iconMode = true;
    }
  }

  const listButtonStyle = iconMode ? {
    //margin: '0 10px 0 3px',
    alignItems: 'center',
    justifyContent: 'center',
    ...style
  } : style;

  const detailButtonStyle = iconMode ? {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    ...style
  } : style;

  const buttonStyle = isDetail ? detailButtonStyle : listButtonStyle;

  if (baseClass) {
    return (
      <IfmsButton 
        {...other} 
        baseClass={baseClass} 
        onClick={handleClick} 
        disabled={isDisabled} 
        preventDuplicateAction={true}
        style={buttonStyle}
      >
        {label}
      </IfmsButton>
    );
  } else if (useOnlyIcon) {
    let icon = AddIcon2;
    if(other['data-tooltip'] === 'New'){
      if(!newStyle) icon = appcontext.useDarkMode ? AddIcon2_D : AddIcon2;
      else  icon = appcontext.useDarkMode ? AddIcon5_D : AddIcon5;
    }else if(other['data-tooltip'] === 'Amend' || other['data-tooltip'] === 'Edit'){
       if(!newStyle) icon = appcontext.useDarkMode? EditIcon2_D : EditIcon2;
       else icon = appcontext.useDarkMode? (other['id'] === 'acPayroll2-edit-button' ? EditIcon5_DA : EditIcon5_D) : EditIcon5;
    }else if(other['data-tooltip'] === 'View'){
       if(!newStyle)  icon = appcontext.useDarkMode? ViewIcon2_D : ViewIcon2;
      else icon = appcontext.useDarkMode? (other['id'] === 'cpay-redirect-button' || other['id'] === 'apay-redirect-button' ? ViewIcon5_DA : ViewIcon5_D) : ViewIcon5;
    }else if(other['data-tooltip'] === 'Credit'){
      icon = appcontext.useDarkMode? CreditIcon2_D : CreditIcon2;
    }else if(other['data-tooltip'] === 'Give Credit' || other['data-tooltip'] === 'Receive Credit'){
      icon = appcontext.useDarkMode? GCreditIcon2_D : GCreditIcon2;
    }
    other['size'] = 'imgStyle';

    return (
      iconMode ?
        <IfmsButton 
          {...other} 
          baseClass={'ifms-icon-button'} 
          onClick={handleClick} 
          disabled={isDisabled} 
          icon={icon} 
          preventDuplicateAction={true}
          style={buttonStyle}
          iconHeight={newStyle ? "24px" : "20px"}
        />
        :
        (appcontext.suntekSettings?.company?.billingsTabVersion === 2 ?
          <PrimaryButton 
            {...other} 
            size="extrasmall"  
            icon={''}
            onClick={handleClick} 
            disabled={isDisabled} 
            data-tooltip=''
            style={buttonStyle}
          >
            {other['data-tooltip']}
          </PrimaryButton>
          :
          <IfmsButton 
            {...other} 
            baseClass={'ifms-icon-button'} 
            onClick={handleClick} 
            disabled={isDisabled} 
            icon={icon} 
            preventDuplicateAction={true}
            style={buttonStyle}
          />
        )
    );
  } else {
    if (display === 'primary') {
      return (
        <PrimaryButton 
          {...other} 
          onClick={handleClick} 
          disabled={isDisabled} 
          preventDuplicateAction={true}
          style={buttonStyle}
        >
          {label}
        </PrimaryButton>
      );
    } else if (display === 'white') {
      return (
        <WhiteButton 
          {...other} 
          onClick={handleClick} 
          disabled={isDisabled} 
          preventDuplicateAction={true}
          style={buttonStyle}
        >
          {label}
        </WhiteButton>
      );
    } else if (display === 'lightblue') {
      return (
        <LightBlueButton 
          {...other} 
          onClick={handleClick} 
          disabled={isDisabled} 
          preventDuplicateAction={true}
          style={buttonStyle}
        >
          {label}
        </LightBlueButton>
      );
    } else {
      return (
        <SecondaryButton 
          {...other} 
          onClick={handleClick} 
          disabled={isDisabled} 
          preventDuplicateAction={true}
          style={buttonStyle}
        >
          {label}
        </SecondaryButton>
      );
    }
  }
}

UniqueLinkButton.defaultProps = {
  disabled: false,
  display: 'primary',
  size: 'regular',
  viewLink: '',
  operation: '',
  isMainOp: false,
  useOnlyIcon: false,
  iconMode: false,
};

UniqueLinkButton.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  link: PropTypes.string.isRequired,
  viewLink: PropTypes.string.isRequired,
  operation: PropTypes.string.isRequired,
  uniqueLinkPrefixQuery: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  beforeRedirect: PropTypes.func,
  disabled: PropTypes.bool.isRequired,
  display: PropTypes.oneOf(['primary', 'secondary', 'white', 'lightblue', 'secondary-light']),
  size: PropTypes.oneOf(['large', 'small', 'regular','extrasmall']),
  baseClass: PropTypes.string,
  goToAlreadyOpenPage: PropTypes.bool,
  isMainOp: PropTypes.bool,
  useOnlyIcon: PropTypes.bool,
  iconMode: PropTypes.bool,
  isDetail: PropTypes.bool,
};

export default React.memo(UniqueLinkButton);
